/**
 * 거래처 — 탭 두 개 (v2.143.0 · 2026-09-09 · v2.318.0 이름 「거래처 관리」→「거래처」)
 *
 *   🏢 거래처        거래처 마스터(partner_company) — **읽기 sales 이상 · 쓰기 admin + mgmt**(v2.318.0 · 사용자 결정 ⑤ 「전부 읽기 · 쓰기는 admin·mgmt」)
 *                   (사업자번호·주소·세금계산서 메일·사업자등록증 · 통장사본은 admin·mgmt 만) · 거래처 이름을 누르면 거래처 360(상세 창)
 *   👤 거래처 담당자  주소록(partner_contact) — sales 이상 · viewer 는 읽기 전용(v2.197.0)
 *
 * 왜 탭인가: 사용자 지시 「기존 거래처 리스트에 거래처 담당자 메뉴를 추가」 — 메뉴는 화면 하나에 한 줄(v2.135.0 원칙)이라
 *   같은 화면 안의 탭으로 둔다. v2.318.0 전에는 영업 계정에 담당자 탭만 보였다(거래처 마스터는 admin/mgmt 만) —
 *   Salesforce 식 「영업건 → 거래처 → 그 거래처의 모든 것」으로 오가려면 영업도 거래처 360 을 열 수 있어야 해 읽기로 열었다.
 *
 * 주소 `?tab=companies|contacts` — 딜등록 화면의 「주소록에서 관리 →」 링크가 `?tab=contacts` 로 보낸다(검사 그물 check:menu ⑨).
 */

import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { PartnerCompanyTab } from '../components/PartnerCompanyTab';
import { PartnerContactTab } from '../components/PartnerContactTab';
import { LastUpdateBadge } from '../components/LastUpdateBadge';
import { PageTabs } from '../components/ui/PageTabs';

type TabKey = 'companies' | 'contacts';

/** 탭 정의 — 키는 주소(?tab=)와 브라우저 탭 제목(lib/menu.ts TAB_TITLES)에 그대로 쓰인다 */
const TABS: { key: TabKey; label: string; hideForViewer?: boolean }[] = [
  // v2.318.0: 영업에게도(읽기) — viewer 만 못 본다(거래처 마스터는 사용자 결정으로 viewer 에게 닫힘 · v2.197.0)
  { key: 'companies', label: '🏢 거래처', hideForViewer: true },
  { key: 'contacts', label: '👤 거래처 담당자' },
];

export function PartnerCompanyPage() {
  const { user } = useAuth();
  const isAdminMgmt = user?.role === 'admin' || user?.role === 'mgmt';
  const isViewer = user?.role === 'viewer';
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');

  const visibleTabs = TABS.filter((t) => !t.hideForViewer || !isViewer);
  // 첫 탭 = 거래처(Salesforce 의 Accounts 처럼 거래처가 앞) · viewer 는 담당자 주소록만
  const fallback: TabKey = isViewer ? 'contacts' : 'companies';
  const tab: TabKey = visibleTabs.some((t) => t.key === tabParam) ? (tabParam as TabKey) : fallback;

  const selectTab = (k: TabKey): void => {
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', k);
    setSearchParams(sp, { replace: true });
  };

  /* v2.197.0 — viewer 도 들어온다(**읽기 전용**). 종전에는 여기서 「접근 권한 없음」으로 막았다.
     ⭐viewer 에게는 `adminMgmtOnly` 필터 때문에 **담당자 탭 하나만** 보인다(거래처 마스터는 그대로 닫힘).
     ⛔쓰기는 화면에서 감추는 것으로 끝내지 않는다 — /partner-contacts 의 POST·PATCH·DELETE 가 sales 이상이다. */

  return (
    <div className="p-8">
      <h1 className="ui-page-title">거래처</h1>
      <p className="ui-page-sub">
        {tab === 'companies'
          ? '거래처 마스터 목록 — 영업·매입·견적 회사명 자동완성/표준화 기준'
          : '거래처 담당자 주소록 — 영업진행·딜등록에 적은 담당자를 명함처럼 모아 둔 곳 (이름 · 부서 · 직급 · 메일 · 연락처)'}
        {/* v2.197.0 — 조회 전용 계정에게 **왜 단추가 없는지** 말해 준다. 단추만 조용히 감추면 「나는 왜 못 고치지」로 문의가 온다 */}
        {user?.role === 'viewer'
          ? <span className="ml-1 text-amber-700">· <b>조회 전용</b>입니다 — 보기만 가능하고 고치거나 넣을 수는 없습니다.</span>
          : null}
      </p>
      <LastUpdateBadge className="mb-3 mt-1" />

      <PageTabs
        tabs={visibleTabs}
        value={tab}
        onChange={selectTab}
        ariaLabel="거래처 보기"
        right={!isAdminMgmt ? <span className="ui-help">{isViewer ? '거래처 목록(사업자번호·주소)은 경영지원·관리자가 관리합니다' : '거래처 목록은 조회만 — 등록·수정·첨부는 경영지원·관리자가 합니다'}</span> : null}
      />

      {tab === 'companies' && !isViewer ? <PartnerCompanyTab /> : <PartnerContactTab />}
    </div>
  );
}
